/**
 * ============================================================================
 * 電話驗證元件
 * 紫微斗數算命系統 - Phone Verification Component
 * ============================================================================
 * 功能：
 * 1. 顯示驗證碼輸入框（6 位數）
 * 2. 倒數計時器（驗證碼有效期 5 分鐘）
 * 3. 重新發送驗證碼功能（60 秒冷卻）
 * 4. 自動聚焦與格式化
 * ============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  PhoneAndroid as PhoneIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';

interface PhoneVerificationProps {
  /** 電話號碼（用於顯示） */
  phoneNumber: string;
  
  /** 驗證碼長度（預設 6） */
  codeLength?: number;
  
  /** 驗證碼有效期（秒，預設 300 = 5 分鐘） */
  validityDuration?: number;
  
  /** 重新發送冷卻時間（秒，預設 60） */
  resendCooldown?: number;
  
  /** 驗證中狀態 */
  isVerifying: boolean;
  
  /** 錯誤訊息 */
  error?: string | null;
  
  /** 驗證成功回調 */
  onVerify: (code: string) => void;
  
  /** 重新發送驗證碼回調 */
  onResend: () => void;
  
  /** 返回上一步回調 */
  onBack?: () => void;
}

/**
 * 電話驗證元件
 */
export const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phoneNumber,
  codeLength = 6,
  validityDuration = 300, // 5 分鐘
  resendCooldown = 60, // 60 秒
  isVerifying,
  error,
  onVerify,
  onResend,
  onBack,
}) => {
  const [code, setCode] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(validityDuration);
  const [resendTimeRemaining, setResendTimeRemaining] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  
  const codeInputRef = useRef<HTMLInputElement>(null);

  /**
   * 自動聚焦驗證碼輸入框
   */
  useEffect(() => {
    if (codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, []);

  /**
   * 倒數計時器（驗證碼有效期）
   */
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  /**
   * 重新發送冷卻計時器
   */
  useEffect(() => {
    if (resendTimeRemaining > 0) {
      const timer = setInterval(() => {
        setResendTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendTimeRemaining]);

  /**
   * 處理驗證碼輸入
   */
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // 只保留數字
    if (value.length <= codeLength) {
      setCode(value);
      
      // 自動提交（當輸入滿 6 位數時）
      if (value.length === codeLength) {
        onVerify(value);
      }
    }
  };

  /**
   * 處理重新發送
   */
  const handleResend = () => {
    if (resendTimeRemaining === 0) {
      setCode(''); // 清空驗證碼
      setTimeRemaining(validityDuration); // 重置有效期
      setResendTimeRemaining(resendCooldown); // 啟動冷卻
      setIsExpired(false); // 重置過期狀態
      onResend();
    }
  };

  /**
   * 格式化秒數為 MM:SS
   */
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * 計算進度條百分比
   */
  const progress = (timeRemaining / validityDuration) * 100;

  return (
    <Paper
      elevation={24}
      sx={{
        p: 4,
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        maxWidth: 500,
        mx: 'auto',
      }}
    >
      {/* 標題區 */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <PhoneIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
          簡訊驗證
        </Typography>
        <Typography variant="body2" color="text.secondary">
          我們已發送 6 位數驗證碼到
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
          {phoneNumber}
        </Typography>
      </Box>

      {/* 錯誤訊息 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 過期警告 */}
      {isExpired && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          驗證碼已過期，請點擊「重新發送」
        </Alert>
      )}

      {/* 驗證碼輸入框 */}
      <Box sx={{ mb: 3 }}>
        <TextField
          inputRef={codeInputRef}
          fullWidth
          placeholder={`請輸入 ${codeLength} 位數驗證碼`}
          value={code}
          onChange={handleCodeChange}
          disabled={isVerifying || isExpired}
          inputMode="numeric"
          autoComplete="one-time-code"
          sx={{
            '& input': {
              fontSize: '24px',
              fontWeight: 'bold',
              textAlign: 'center',
              letterSpacing: '8px',
            },
          }}
        />
        
        {/* 輸入提示 */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          {code.length} / {codeLength}
        </Typography>
      </Box>

      {/* 倒數計時與進度條 */}
      {!isExpired && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              驗證碼有效期
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                color: timeRemaining < 60 ? 'error.main' : 'text.secondary',
              }}
            >
              {formatTime(timeRemaining)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: timeRemaining < 60 ? 'error.main' : 'primary.main',
              },
            }}
          />
        </Box>
      )}

      {/* 驗證按鈕 */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={() => onVerify(code)}
        disabled={code.length !== codeLength || isVerifying || isExpired}
        startIcon={isVerifying ? <CircularProgress size={20} /> : <SuccessIcon />}
        sx={{ mb: 2, py: 1.5 }}
      >
        {isVerifying ? '驗證中...' : '確認驗證碼'}
      </Button>

      {/* 重新發送按鈕 */}
      <Button
        fullWidth
        variant="outlined"
        onClick={handleResend}
        disabled={resendTimeRemaining > 0}
        startIcon={<RefreshIcon />}
        sx={{ mb: 2 }}
      >
        {resendTimeRemaining > 0
          ? `重新發送 (${resendTimeRemaining}秒)`
          : '重新發送驗證碼'}
      </Button>

      {/* 返回按鈕 */}
      {onBack && (
        <Button
          fullWidth
          variant="text"
          onClick={onBack}
          disabled={isVerifying}
        >
          返回修改電話號碼
        </Button>
      )}

      {/* 提示訊息 */}
      <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          ℹ️ 未收到驗證碼？
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          • 請確認電話號碼正確
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          • 檢查簡訊是否被攔截
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          • 等待 60 秒後可重新發送
        </Typography>
      </Box>
    </Paper>
  );
};
